import reportListHtml from './ReportListComponent.html';
import { initializeHeader } from "../../utils/header-manager";
import { stateManager } from "../../utils/state-manager";
import { showToast, escapeHTML, debounce } from "../../utils/ui-utils";
import { getOrganisationName } from "../../utils/org-utils";
import { resolveDatePreset } from "../../utils/date-presets";

// ────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────

const BADGE_MAP: Record<string, { cls: string; short: string }> = {
    "Profit and Loss Statement": { cls: "badge-pnl",    short: "P&L" },
    "Balance Sheet":             { cls: "badge-bs",     short: "BS" },
    "Bank Statement":            { cls: "badge-bank",   short: "Bank" },
    "Custom Forms":              { cls: "badge-custom", short: "Custom" },
    "General Ledger":            { cls: "badge-gl",     short: "GL" },
    "Bills to Pay":              { cls: "badge-bills",  short: "Bills" },
    "Inbox":                     { cls: "badge-bank",   short: "Inbox" },
    "Transactions":              { cls: "badge-custom", short: "Txns" },
};

const EMPTY_STATE = `
<div class="empty-state">
  <div class="empty-state-icon">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  </div>
  <p class="empty-state-title">No reports yet</p>
  <p class="empty-state-desc">Run your first report with the New button above.</p>
</div>`;

const SKELETON = `
<div class="skeleton-card"><div class="skeleton skeleton-line" style="width:58%;"></div><div class="skeleton skeleton-line" style="width:38%;"></div></div>
<div class="skeleton-card"><div class="skeleton skeleton-line" style="width:52%;"></div><div class="skeleton skeleton-line" style="width:34%;"></div></div>
<div class="skeleton-card"><div class="skeleton skeleton-line" style="width:63%;"></div><div class="skeleton skeleton-line" style="width:41%;"></div></div>`;

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function relativeDate(iso: string): string {
    try {
        const ms = Date.now() - new Date(iso).getTime();
        const m = Math.floor(ms / 60000);
        if (m < 1)  return 'just now';
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d < 7)  return `${d}d ago`;
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return iso.slice(0, 10); }
}

/** A Transactions run's search and filter-panel conditions, in the words the web panel uses. */
function transactionFilterSummary(params: any): string {
    const parts: string[] = [];
    if (params.search) parts.push(`Search “${params.search}”`);
    if (params.txn) parts.push(`Transaction contains “${params.txn}”`);
    if (params.codedToNames?.length) parts.push(`Coded to ${params.codedToNames.join(" or ")}`);
    if (params.details) parts.push(`Details contains “${params.details}”`);
    const min = params.amountMin, max = params.amountMax;
    const hasMin = min !== undefined && min !== null, hasMax = max !== undefined && max !== null;
    if (hasMin && hasMax) parts.push(min === max ? `Amount is exactly ${min}` : `Amount is between ${min} and ${max}`);
    else if (hasMin) parts.push(`Amount is at least ${min}`);
    else if (hasMax) parts.push(`Amount is at most ${max}`);
    if (params.notInReport) parts.push("Not in an expense report");
    else if (params.expenseReportNames?.length) parts.push(`Expense report is ${params.expenseReportNames.join(" or ")}`);
    else if (params.expenseReport) parts.push(`Expense report name contains “${params.expenseReport}”`);
    return parts.join(", ");
}

function renderReportCard(row: any[], idx: number): string {
    let params: any;
    try { params = JSON.parse(row[2]); }
    catch { return `<div class="error-text">Corrupt entry (row ${idx + 2})</div>`; }

    const reportName = String(row[1]);
    const sheetName  = escapeHTML(String(row[3]));
    const orgName    = escapeHTML(params.organizationName || params.organisationName || '');
    const badge      = BADGE_MAP[reportName];
    const rowIdx     = idx + 2;

    let details = '';
    if (params.from && params.to)
        details += `<div><b>Period:</b> ${escapeHTML(params.from)} – ${escapeHTML(params.to)}</div>`;
    if (params.periods)
        details += `<div><b>Comparisons:</b> ${escapeHTML(String(params.periods))}</div>`;
    if (orgName)
        details += `<div><b>Organisation:</b> ${orgName}</div>`;
    if (reportName === "Bills to Pay")
        details += `<div><b>Filter:</b> ${escapeHTML(params.filterName || "All bills")}</div>`;
    if (reportName === "Inbox") {
        const view = ({ "for-review": "For Review", processed: "Processed", all: "All documents" } as any)[params.inboxView] || "For Review";
        details += `<div><b>Show:</b> ${escapeHTML(view)}</div>`;
        details += `<div><b>Filter:</b> ${escapeHTML(params.filterName || "No filter")}</div>`;
    }
    if (reportName === "Transactions") {
        const names: string[] = params.accountNames || [];
        details += `<div><b>Accounts:</b> ${escapeHTML(names.length ? names.join(", ") : "All accounts")}</div>`;
        if (params.datePreset === "all_dates") details += `<div><b>Period:</b> All dates</div>`;
        const filters = transactionFilterSummary(params);
        if (filters) details += `<div><b>Filters:</b> ${escapeHTML(filters)}</div>`;
        if (params.showSuggestedCoding) details += `<div><b>Suggested coding:</b> Shown</div>`;
    }
    details += `<div><b>Sheet:</b> ${sheetName}</div>`;

    return `
<div class="existing-report-row report-card" data-sheet="${sheetName}" data-type="${escapeHTML(reportName)}" data-row="${rowIdx}">
  <div class="report-card-header">
    <div class="report-card-meta">
      <span class="report-card-title">${escapeHTML(reportName)}</span>
      <span class="report-card-date" title="${escapeHTML(String(row[0]).slice(0,19).replace('T',' '))}">${relativeDate(String(row[0]))}</span>
    </div>
    <div style="display:flex;align-items:center;gap:0.3rem;flex-shrink:0;">
      ${badge ? `<span class="report-type-badge ${badge.cls}">${badge.short}</span>` : ''}
      <div class="report-card-actions">
        <span class="refresh-report-icon report-card-icon" title="Refresh" data-row="${rowIdx}">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10M1 14l5.37 4.36A9 9 0 0 0 20.49 15"/></svg>
        </span>
        <span class="edit-report-icon report-card-icon" title="Edit &amp; re-run" data-row="${rowIdx}">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19.5 2 20l.5-5L16.5 3.5z"/></svg>
        </span>
        <span class="delete-report-icon report-card-icon" title="Delete report" data-row="${rowIdx}">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        </span>
      </div>
    </div>
  </div>
  <div class="report-card-details">${details}</div>
  <div class="report-card-confirm" hidden>
    <p class="report-card-confirm__text">Delete this report and its <b>${sheetName}</b> sheet? This cannot be undone.</p>
    <div class="report-card-confirm__actions">
      <button class="rcc-btn" data-act="cancel" type="button">Cancel</button>
      <button class="rcc-btn rcc-btn--danger" data-act="confirm" type="button">Delete</button>
    </div>
  </div>
</div>`;
}

/**
 * SD-1199. Re-run one recorded report onto the sheet it already lives on.
 *
 * The five-branch dispatch that used to sit here is gone: the server picks the renderer from the
 * report name, so this no longer needs to know that a Balance Sheet is fetched differently to a
 * Bank Statement. What is left is the part that is genuinely the add-in's business -- resolving
 * the organisation name, re-resolving the relative date preset in the user's own timezone, and
 * deciding whether the history row needs rewriting.
 */
async function refreshReport(reportName: string, params: any, sheetName?: string): Promise<void> {
    const r = await import("../ReportRenderer/ReportRenderer");
    const { renderReport } = await import("../../services/render-report");

    const orgId = params.organisationId || params.organisationID || params.organizationId;
    // The report header prints the organisation name. Runs recorded before that name
    // was stored carry only the id, so resolve it rather than letting the server
    // render a report headed by a blank line.
    const organisationName = params.organisationName || params.organizationName ||
        await getOrganisationName(orgId);
    // A run records the relative preset it was built with ("This Month", "YTD", …)
    // rather than the dates that preset resolved to, so re-resolve against today.
    // Runs with a custom range resolve to null and keep their stored dates.
    //
    // Resolved here rather than on the server on purpose: this is the user's local midnight,
    // which the server cannot observe. It sends the dates it settled on.
    const range = resolveDatePreset(params.datePreset);
    // A re-run must write back to the sheet the report already lives on, not to
    // whichever tab happens to be selected when the refresh icon is clicked.
    const p = { ...params, ...(range ?? {}), organisationName, ...(sheetName ? { sheetName } : {}) };

    await renderReport(reportName, p, sheetName);

    // Rewrite the history row after every successful run, not only one whose dates moved.
    // An edit re-run changes whatever the user changed on the form — output type, display
    // mode, comparison periods, the organisation — and the row has to carry that forward, or
    // the next refresh silently replays the parameters the report was first built with. The
    // Timestamp counts too: a refresh with unchanged dates still rebuilt the sheet.
    //
    // `p` is exactly what was rendered, minus the sheetName the server call needs — the
    // history sheet keeps that in its own column, so storing it in the parameter bag as well
    // would put two copies of the same fact in the row.
    if (sheetName) {
        const { sheetName: _renderTarget, ...rendered } = p;
        await r.logReportRun({
            reportName,
            parameters: rendered,
            sheetName,
            reportLocation: reportName,
        });
    }
}

async function attachReportCardHandlers(allValues: any[][]): Promise<void> {
    // Click card → navigate to sheet
    Array.from(document.getElementsByClassName("existing-report-row")).forEach(card => {
        card.addEventListener("click", async (e) => {
            if ((e.target as HTMLElement).closest('.report-card-icon')) return;
            const sheet = (card as HTMLElement).dataset.sheet;
            if (!sheet) return;
            try {
                await Excel.run(async ctx => {
                    const ws = ctx.workbook.worksheets.getItem(sheet);
                    ws.activate();
                    ws.getRange("A1").select();
                    await ctx.sync();
                });
            } catch { showToast("Could not navigate to sheet: " + sheet); }
        });
    });

    // Refresh icons
    Array.from(document.getElementsByClassName("refresh-report-icon")).forEach(icon => {
        icon.addEventListener("click", async (e) => {
            e.stopPropagation();
            const btn = e.currentTarget as HTMLElement;
            const orig = btn.innerHTML;
            btn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" class="spin-icon" style="animation:spin 0.7s linear infinite;"><polyline points="23 4 23 10 17 10"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10M1 14l5.37 4.36A9 9 0 0 0 20.49 15"/></svg>`;
            const rowIdx = parseInt(btn.dataset.row!);
            const row = allValues[rowIdx - 1];
            try {
                const params = JSON.parse(row[2]);
                // Prefer the OrgId column (F) so re-runs target the report's own org.
                if (row[5]) params.organisationId = params.organisationId || row[5];
                await refreshReport(row[1], params, row[3] ? String(row[3]) : undefined);
                btn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
                setTimeout(() => { btn.innerHTML = orig; }, 1400);
            } catch {
                btn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;
                showToast("Refresh failed. Please try again.");
                setTimeout(() => { btn.innerHTML = orig; }, 1800);
            }
        });
    });

    // Edit icons
    Array.from(document.getElementsByClassName("edit-report-icon")).forEach(icon => {
        icon.addEventListener("click", async (e) => {
            e.stopPropagation();
            const btn = e.currentTarget as HTMLElement;
            const rowIdx = parseInt(btn.dataset.row!);
            const row = allValues[rowIdx - 1];
            const storedParams = JSON.parse(row[2]);
            // Prefer the OrgId column (F) so the params form prefills the report's
            // own organisation rather than whatever org is active in localStorage.
            if (row[5]) storedParams.organisationId = storedParams.organisationId || row[5];
            const targetSheet = row[3] ? String(row[3]) : undefined;
            const { loadReportParametersComponent } = await import("../ReportParameters/ReportParametersComponent");
            loadReportParametersComponent(row[1], async (newParams) => {
                await refreshReport(row[1], { ...storedParams, ...newParams }, targetSheet);
                showLandingView();
            }, storedParams, undefined, targetSheet);
        });
    });

    // Delete icons — reveal the card's own confirmation strip rather than firing straight
    // away. This removes a worksheet and everything on it, so it is not a click to make by
    // accident, and an inline strip works where window.confirm is unreliable in an Office
    // task pane.
    Array.from(document.getElementsByClassName("delete-report-icon")).forEach(icon => {
        icon.addEventListener("click", (e) => {
            e.stopPropagation();
            const card = (e.currentTarget as HTMLElement).closest('.existing-report-row');
            const strip = card?.querySelector<HTMLElement>('.report-card-confirm');
            if (strip) strip.hidden = false;
        });
    });

    // Confirmation strips
    Array.from(document.getElementsByClassName("report-card-confirm")).forEach(el => {
        const strip = el as HTMLElement;
        strip.addEventListener("click", async (e) => {
            // The card itself navigates to its worksheet on click; neither button should.
            e.stopPropagation();
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-act]');
            if (!btn) return;

            if (btn.dataset.act === 'cancel') {
                strip.hidden = true;
                return;
            }

            const sheet = strip.closest<HTMLElement>('.existing-report-row')?.dataset.sheet;
            if (!sheet) return;

            btn.disabled = true;
            btn.textContent = 'Deleting…';
            try {
                const { deleteReportRun } = await import("../ReportRenderer/ReportRenderer");
                await deleteReportRun(sheet);
                showToast(`Deleted “${sheet}”.`);
                const listEl = document.getElementById("landing-reports-list");
                if (listEl) await loadReportHistoryIntoContainer(listEl);
            } catch (err) {
                btn.disabled = false;
                btn.textContent = 'Delete';
                // Most often Excel refusing to delete the workbook's last visible sheet.
                const detail = err instanceof Error && err.message ? err.message : "";
                showToast(detail ? `Could not delete: ${detail}` : "Could not delete this report.", true);
            }
        });
    });
}

function setupSearchAndFilter(): void {
    const searchInput = document.getElementById('reports-search') as HTMLInputElement;
    const filterChips = document.querySelectorAll('.filter-chip');
    const noResults   = document.getElementById('no-results-state');
    let activeFilter  = 'all';
    let searchTerm    = '';

    function applyFilters(): void {
        const cards = Array.from(document.querySelectorAll<HTMLElement>('.existing-report-row'));
        if (!cards.length) return;

        let visible = 0;
        cards.forEach(card => {
            const type  = card.dataset.type || '';
            const title = (card.querySelector('.report-card-title')?.textContent || '').toLowerCase();
            const matchF = activeFilter === 'all' || type === activeFilter;
            const matchS = !searchTerm || title.includes(searchTerm);
            card.style.display = matchF && matchS ? '' : 'none';
            if (matchF && matchS) visible++;
        });

        if (noResults) noResults.style.display = visible === 0 ? 'block' : 'none';
    }

    searchInput?.addEventListener('input', debounce(() => {
        searchTerm = searchInput.value.toLowerCase().trim();
        applyFilters();
    }, 300));

    filterChips.forEach(chip => {
        chip.addEventListener('click', () => {
            filterChips.forEach(c => c.classList.remove('active'));
            chip.classList.add('active');
            activeFilter = (chip as HTMLElement).dataset.filter || 'all';
            applyFilters();
        });
    });
}

async function loadReportHistoryIntoContainer(listEl: HTMLElement): Promise<void> {
    listEl.innerHTML = SKELETON;
    try {
        await Excel.run(async ctx => {
            let sheet: Excel.Worksheet;
            try {
                sheet = ctx.workbook.worksheets.getItem("__SyncedExcel_ReportHistory");
                sheet.load("name");
                await ctx.sync();
            } catch {
                listEl.innerHTML = EMPTY_STATE;
                return;
            }
            const range = sheet.getUsedRange();
            range.load(["values", "rowCount"]);
            await ctx.sync();

            if (range.rowCount <= 1) {
                listEl.innerHTML = EMPTY_STATE;
                return;
            }

            const rows = range.values.slice(1);
            // Reverse so newest is first
            const cardsHtml = [...rows].reverse().map((row, idx) => renderReportCard(row, rows.length - 1 - idx)).join('');
            listEl.innerHTML = `<div class="report-cards-list">${cardsHtml}</div>`;

            // Update count label
            const countEl = document.getElementById('reports-count-label');
            if (countEl) countEl.textContent = `${rows.length} report${rows.length !== 1 ? 's' : ''} found`;

            await attachReportCardHandlers(range.values);
            setupSearchAndFilter();
        });
    } catch {
        showToast("Error loading previous reports.");
        listEl.innerHTML = EMPTY_STATE;
    }
}

// ────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────

export async function loadReportsListComponent(_onReportSelected?: (report: string) => void, _preselected?: string): Promise<void> {
    const container = document.getElementById("component-container");
    if (!container) return;

    initializeHeader();
    stateManager.setActiveTab('create-report');
    stateManager.setView('report-selection');

    container.innerHTML = reportListHtml;

    const typeSelection  = document.getElementById("report-type-selection")!;
    const paramsScreen   = document.getElementById("report-params-screen")!;
    const paramsContainer = document.getElementById("report-params-container")!;
    const reportNameEl   = document.getElementById("params-report-name")!;

    // Wire up report type card clicks
    container.querySelectorAll<HTMLButtonElement>('.report-type-card').forEach(card => {
        card.addEventListener('click', async () => {
            const report = card.dataset.report!;

            // Highlight selected card
            container.querySelectorAll('.report-type-card').forEach(c => c.classList.remove('selected'));
            card.classList.add('selected');

            // Switch to params screen
            typeSelection.style.display = 'none';
            paramsScreen.style.display  = 'block';
            reportNameEl.textContent = `${report}`;

            // Load params form into the inline container
            paramsContainer.innerHTML = '<div style="text-align:center;padding:1.5rem 0;"><div class="modern-spinner"></div></div>';
            const { loadReportParametersComponent } = await import('../ReportParameters/ReportParametersComponent');
            await loadReportParametersComponent(report, () => {}, undefined, paramsContainer);
        });
    });

    // Back button
    document.getElementById("back-to-types")?.addEventListener("click", () => {
        paramsScreen.style.display   = 'none';
        typeSelection.style.display  = 'block';
        paramsContainer.innerHTML    = '';
        container.querySelectorAll('.report-type-card').forEach(c => c.classList.remove('selected'));
    });

    // Show the selection section
    const selSec = document.getElementById("report-selection-section");
    const landSec = document.getElementById("landing-section");
    if (selSec)  selSec.style.display  = 'block';
    if (landSec) landSec.style.display = 'none';
}

export async function showLandingView(): Promise<void> {
    initializeHeader();
    stateManager.setActiveTab('my-reports');
    stateManager.setView('landing');

    // Load HTML if not already in DOM
    let landingSection = document.getElementById("landing-section");
    if (!landingSection) {
        const container = document.getElementById("component-container");
        if (!container) return;
        container.innerHTML = reportListHtml;
        landingSection = document.getElementById("landing-section");
    }

    const selSec = document.getElementById("report-selection-section");
    if (selSec)  selSec.style.display  = 'none';
    if (landingSection) landingSection.style.display = 'block';

    // Reset search/filter UI
    const searchInput = document.getElementById('reports-search') as HTMLInputElement;
    if (searchInput) searchInput.value = '';
    document.querySelectorAll('.filter-chip').forEach((c, i) => c.classList.toggle('active', i === 0));

    const listEl = document.getElementById("landing-reports-list");
    if (!listEl) return;

    await loadReportHistoryIntoContainer(listEl);

    // Wire the header actions once. showLandingView runs on every return to the list, but
    // the landing section itself is only rebuilt when it is missing from the DOM — so
    // without this guard each visit stacked another listener on the same two buttons and a
    // single refresh click fired as many reloads as the user had visited the tab.
    const headerActions = document.querySelector<HTMLElement>('#landing-section .page-header-actions');
    if (headerActions && !headerActions.dataset.wired) {
        headerActions.dataset.wired = 'true';

        document.getElementById("refresh-reports-btn")?.addEventListener("click", async () => {
            const el = document.getElementById("landing-reports-list");
            if (el) await loadReportHistoryIntoContainer(el);
        });

        // Create lives here now rather than in the bottom nav.
        document.getElementById("new-report-btn")?.addEventListener("click", () => {
            loadReportsListComponent(() => {});
        });
    }
}
